/**
 * Allowing a sender whose mail a triage email address refused: the refusal links here (`/allow-sender?triage_email_address=…&sender=…`). An admin of
 * the address's organization is asked whether to allow the address (or everyone at its domain); nothing changes without
 * the click. Allowing takes in the mail held from the sender meanwhile (as Allow does on the unknown senders' screen);
 * a sender whose mail isn't verified is allowed only with a second, explicit choice (an address, never its domain).
 * Anyone else learns nothing about the address: the server answers them as if it weren't there.
 */
import { useState } from "preact/hooks";
import { api } from "../api/client.ts";
import type { AllowedAs, Allowed as AllowedOut, SenderCheck } from "../api/types.gen.ts";
import { t } from "../i18n/index.ts";
import { spaceUrl } from "../lib/paths.ts";
import { useAction, useResource } from "../lib/resource.ts";
import { location, navigate } from "../lib/router.ts";
import { homeUrl } from "../lib/state.ts";
import { toast } from "../lib/toast.ts";
import { useId } from "../lib/uid.ts";
import { Button, Link } from "../ui/Button.tsx";
import { Checkbox } from "../ui/form.tsx";
import { EmptyState, ErrorBox, Loading } from "../ui/misc.tsx";
import { LINK } from "../ui/styles.ts";

/** What the server answers when there's nothing for you here (no such triage email address, not your org's, not an address). */
const NOTHING = new Set([404, 422]);

/** Where the sender would send to: the space, and its organization when it's called something else. */
const placeOf = (c: SenderCheck): string =>
  c.space_name === c.org_name ? c.org_name : t("allowSender.place", { space: c.space_name, org: c.org_name });

/** Everyone at the address's domain, as the allowlist puts it (`@example.com`). */
const domainOf = (address: string): string => `@${address.split("@").pop() ?? ""}`;

/** That the sender may send, and how. */
const allowedText = (c: SenderCheck, as: AllowedAs): string =>
  t(`allowSender.allowed.${as}`, { sender: c.sender, place: placeOf(c), domain: domainOf(c.sender) });

/** The sender may send now: how, and the organization's allowed senders. */
function Allowed({ check, as }: { check: SenderCheck; as: AllowedAs }) {
  return (
    <div class="flex flex-col gap-3 rounded-lg border border-zinc-200 bg-white p-5">
      <p class="font-medium text-zinc-900">{allowedText(check, as)}</p>
      <p class="text-sm text-zinc-600">{t("allowSender.membersAlways")}</p>
      <Link href={spaceUrl({ id: check.space }, "/settings/org")} class={`${LINK} self-start text-sm`}>
        {t("allowSender.seeList")}
      </Link>
    </div>
  );
}

/** The question: allow the address, or everyone at its domain (not a free email service's), or neither. */
function Ask({ check, onAllowed }: { check: SenderCheck; onAllowed: (as: AllowedAs) => void }) {
  const [unverified, setUnverified] = useState(false);
  const [allow, busy] = useAction(async (domain: boolean) => {
    const r = await api.post<AllowedOut>(`/orgs/${check.org}/allowlist`, {
      sender: check.sender,
      domain,
      unverified: check.unverified,
    });
    const as: AllowedAs = domain ? "domain" : "address";
    toast(
      r.released > 0
        ? `${allowedText(check, as)} ${t("allowSender.released", { count: r.released })}`
        : allowedText(check, as),
    );
    onAllowed(as);
  });
  const titleId = useId();
  return (
    <section
      aria-labelledby={titleId}
      class="flex flex-col gap-4 rounded-lg border border-zinc-200 bg-white p-5 shadow-sm"
    >
      <h1 id={titleId} class="text-lg font-semibold break-words text-zinc-900">
        {t("allowSender.title", { sender: check.sender, place: placeOf(check) })}
      </h1>
      <p class="text-sm text-zinc-700">{t("allowSender.explain", { address: check.triage_email_address })}</p>
      {check.held > 0 ? <p class="text-sm text-zinc-700">{t("allowSender.held", { count: check.held })}</p> : null}
      {check.unverified ? (
        <div class="flex flex-col gap-2 rounded-md bg-amber-50 p-3 text-sm text-amber-950">
          <p>{t("held.unverifiedWarning", { sender: check.sender })}</p>
          <Checkbox label={t("held.acceptUnverified")} checked={unverified} onCheck={setUnverified} />
        </div>
      ) : null}
      <div class="flex flex-wrap justify-end gap-2 pt-2">
        <Button onClick={() => navigate(homeUrl())}>{t("common.cancel")}</Button>
        {check.domain && !check.unverified ? (
          <Button busy={busy} onClick={() => allow(true)}>
            {t("allowSender.allowDomain", { domain: check.domain })}
          </Button>
        ) : null}
        <Button variant="primary" busy={busy} disabled={check.unverified && !unverified} onClick={() => allow(false)}>
          {t("allowSender.allowAddress")}
        </Button>
      </div>
    </section>
  );
}

export function AllowSender() {
  const q = location.value.searchParams;
  const address = q.get("triage_email_address") ?? "";
  const sender = q.get("sender") ?? "";
  const r = useResource(`allow-sender:${address}:${sender}`, (signal) =>
    api.get<SenderCheck>("/allow-sender", { triage_email_address: address, sender }, signal),
  );
  const [allowed, setAllowed] = useState<AllowedAs | null>(null);
  if (r.error) {
    return NOTHING.has(r.error.status) ? (
      <EmptyState title={t("allowSender.nothing")}>{t("allowSender.nothingHint")}</EmptyState>
    ) : (
      <ErrorBox error={r.error} onRetry={r.reload} />
    );
  }
  if (!r.data) {
    return <Loading />;
  }
  const as = allowed ?? r.data.allowed;
  return as ? <Allowed check={r.data} as={as} /> : <Ask check={r.data} onAllowed={setAllowed} />;
}
